import { cn } from '@/lib/utils';
import type { ChatMessage } from './hooks/use-realtime-chat';
import { Bell, Trash2 } from 'lucide-react';
import Linkify from 'linkify-react';
import { ChatAttachment } from './chat-attachment';

interface ChatMessageItemProps {
    message: ChatMessage
    isOwnMessage: boolean
    showHeader: boolean
    onDelete?: () => void
    /** Called when an inline image finishes loading (the message grows after render). */
    onImageLoad?: () => void
}

export const ChatMessageItem = ({ message, isOwnMessage, showHeader, onDelete, onImageLoad }: ChatMessageItemProps) => {
    return (
        <div className={`flex mt-2 justify-start`}>
            <div
                className={cn('max-w-[95%] w-fit flex flex-col gap-1')}
            >
                {showHeader && (
                    <div
                        className={cn('flex items-center gap-2 text-xs')}
                    >
                        <span className={'font-medium'}>{message.userName}</span>
                        <span className="text-foreground/50 text-xs">
                            {new Date(message.sentAt).toLocaleTimeString('sv-SE', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: false,
                            })}
                        </span>
                    </div>
                )}
                <div className="relative flex flex-row items-center gap-1 min-w-0">
                    {message.withNotification && <Bell className="absolute size-5 -left-[1.5rem]" />}
                    <div
                        className={cn(
                            'py-2 px-3 rounded-xl text-sm w-fit max-w-full flex flex-col gap-2',
                            isOwnMessage ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'
                        )}
                    >
                        {message.file && (
                            <ChatAttachment
                                file={message.file}
                                isOwnMessage={isOwnMessage}
                                onImageLoad={onImageLoad}
                            />
                        )}
                        {message.content && (
                            // Linkify renders a fragment; the wrapper keeps text and links inline
                            // instead of becoming separate flex items.
                            <div className="whitespace-pre-wrap break-words">
                                <Linkify options={{
                                    target: "_blank",
                                    defaultProtocol: "https",
                                    rel: "noopener noreferrer",
                                    className: cn(
                                        "text-primary underline hover:no-underline break-all",
                                        isOwnMessage && "text-primary-foreground"
                                    )
                                }}>
                                    {message.content}
                                </Linkify>
                            </div>
                        )}
                    </div>
                    {isOwnMessage && onDelete && (
                        <button
                            type="button"
                            onClick={onDelete}
                            aria-label="Ta bort meddelande"
                            className="text-muted-foreground hover:text-destructive shrink-0"
                        >
                            <Trash2 className="size-4" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};
